import React, { useState, useEffect } from "react";
import { X, AlertTriangle, DollarSign, Calendar } from "lucide-react";
import api from "@/services/api";

const BudgetAllocationModal = ({ request, onClose, onSuccess }) => {
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState("");
  const [subsidiary, setSubsidiary] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const SUBSIDIARIES = [
    { value: "puffin_group", label: "Puffin Group Ltd" },
    { value: "buildwise", label: "Buildwise Construction Ltd" },
    { value: "laser_shield", label: "Laser Shield Ltd." },
    { value: "maz_logistics", label: "Maz Logistics Ltd." },
    { value: "puffin_energy", label: "Puffin Energy Ltd." },
    { value: "swingtide", label: "Swingtide Consulting Ltd" },
    { value: "puffin_agro", label: "Puffin Agro Allied" },
  ];

  useEffect(() => {
    fetchBudgets();
  }, [request]);

  const fetchBudgets = async () => {
    try {
      const response = await api.get("/finance-aggregation/budgets/active", {
        params: { min_amount: request.amount },
      });

      const data = response.data;
      if (data.success) {
        setBudgets(data.data.budgets);
      }
    } catch (error) {
      console.error("Error fetching budgets:", error);
      setError("Failed to load budgets");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedBudget) {
      setError("Please select a budget");
      return;
    }

    if (!subsidiary) {
      setError("Please select a subsidiary");
      return;
    }

    if (!comments.trim()) {
      setError("Please provide approval comments");
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        `/purchase-requests/${request.request_id}/finance`,
        {
          action: "approve",
          // The backend resolves this against Budget.budget_code OR Budget.id,
          // so the selected budget's numeric id works here directly.
          budget_code: parseInt(selectedBudget),
          subsidiary,
          comments,
        },
      );

      const data = response.data;

      if (data.success) {
        onSuccess();
      } else {
        setError(data.message || "Failed to approve request");
      }
    } catch (error) {
      console.error("Error approving request:", error);
      setError("Failed to approve request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const selectedBudgetData = budgets.find(
    (b) => b.id === parseInt(selectedBudget),
  );
  const insufficientFunds =
    selectedBudgetData &&
    parseFloat(selectedBudgetData.available_amount) <
      parseFloat(request.amount);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            Approve Purchase Request
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Request Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Request Summary
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Request ID:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {request.request_id}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Amount:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {formatCurrency(request.amount)}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Department:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {request.department}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Requester:</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {request.requester?.name}
                </span>
              </div>
            </div>
            <div className="mt-3">
              <span className="text-sm text-gray-500">Item/Service:</span>
              <p className="mt-1 text-sm text-gray-900">
                {request.item_service_requested}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Budget Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Allocation <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedBudget}
              onChange={(e) => setSelectedBudget(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select Budget</option>
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.id}>
                  {budget.name} - Available:{" "}
                  {formatCurrency(budget.available_amount)}(
                  {formatDate(budget.period_start)} to{" "}
                  {formatDate(budget.period_end)})
                </option>
              ))}
            </select>

            {budgets.length === 0 && (
              <p className="mt-2 text-sm text-yellow-600">
                No budgets available with sufficient funds for this request
                amount.
              </p>
            )}

            {insufficientFunds && (
              <div className="mt-2 flex items-start text-red-600 text-sm">
                <AlertTriangle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                <span>
                  Insufficient budget funds. Available:{" "}
                  {formatCurrency(selectedBudgetData.available_amount)},
                  Required: {formatCurrency(request.amount)}
                </span>
              </div>
            )}

            {selectedBudgetData && !insufficientFunds && (
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <DollarSign className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Budget Details:</p>
                    <p className="mt-1">
                      Budgeted:{" "}
                      {formatCurrency(selectedBudgetData.budgeted_amount)} |
                      Spent: {formatCurrency(selectedBudgetData.actual_amount)}{" "}
                      | Committed:{" "}
                      {formatCurrency(selectedBudgetData.committed_amount)}
                    </p>
                    <p className="mt-1">
                      After approval, available will be:{" "}
                      {formatCurrency(
                        parseFloat(selectedBudgetData.available_amount) -
                          parseFloat(request.amount),
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subsidiary Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subsidiary <span className="text-red-500">*</span>
            </label>
            <select
              value={subsidiary}
              onChange={(e) => setSubsidiary(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select Subsidiary</option>
              {SUBSIDIARIES.map((sub) => (
                <option key={sub.value} value={sub.value}>
                  {sub.label}
                </option>
              ))}
            </select>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Approval Comments <span className="text-red-500">*</span>
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Provide comments for this approval..."
              required
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !selectedBudget ||
                !subsidiary ||
                !comments.trim() ||
                insufficientFunds ||
                budgets.length === 0
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Approving...</span>
                </>
              ) : (
                <span>Approve & Allocate Budget</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetAllocationModal;
